'use client'

export type Period = "1" | "6" | "12";
export type PlanKey = "base" | "pro" | "proPlus" | "genius";

export const PERIODS: { key: Period; label: string; note: string; best?: boolean }[] = [
  { key: "1", label: "1 месяц", note: "Оплата за месяц" },
  { key: "6", label: "6 месяцев", note: "Оплата сразу за весь период" },
  { key: "12", label: "12 месяцев", note: "Оплата сразу за весь период", best: true },
];

export const PRICES: Record<PlanKey, Record<Period, number>> = {
  base: { "1": 4990, "6": 4490, "12": 3990 },
  pro: { "1": 10990, "6": 9890, "12": 8790 },
  proPlus: { "1": 13990, "6": 12590, "12": 11190 },
  genius: { "1": 15990, "6": 14390, "12": 12790 },
};

export const formatPrice = (value: number) => `${value.toLocaleString("ru-RU")} ₽`;

export interface Plan {
  key: PlanKey;
  name: string;
  ladder: string;
  meaning: string;
  subtitle: string;
  idea: string;
  priceFrom?: boolean;
  badge?: string;
  visual: string;
  benefits: string[];
  clientAccess: string;
  ai: string;
  aiBalance: string;
  storage: { internal: string; external: string };
  modules: string;
  cta: string;
  micro: string;
}

export const PLANS: Plan[] = [
  {
    key: "base",
    name: "BASE",
    ladder: "Начать",
    meaning: "Система управления + готовое приложение бизнеса.",
    subtitle:
      "Управляйте клиентами, продажами и процессами и запустите стандартное приложение в стиле своего бренда.",
    idea: "Готовая система и приложение со связанными страницами, навигацией, хедером и футером. Настройте бренд и наполните каталог — основа для запуска уже есть.",
    visual: "Система управления · CRM · готовое приложение · desktop и mobile для команды",
    benefits: [
      "Система управления, CRM, клиенты, сделки и задачи",
      "Готовое приложение со стандартными страницами",
      "Каталог товаров и услуг без ограничения по количеству",
      "Личный кабинет, лояльность, рефералы и документы",
      "Брендирование приложения и AI-помощь",
    ],
    clientAccess: "Стандартное приложение: главная, каталог, личный кабинет, лояльность, рефералы и системные документы. Глобальное брендирование и экран загрузки с логотипом.",
    ai: "AI-помощь для контента и клиентский AI-помощник по токенной модели",
    aiBalance: "$1 в месяц включено",
    storage: { internal: "1 ГБ", external: "2 ГБ" },
    modules: "Стандартные страницы включены; дополнительные расширения — с PRO",
    cta: "Выбрать BASE",
    micro: "Для запуска системы управления и готового клиентского приложения.",
  },
  {
    key: "pro",
    name: "PRO",
    ladder: "Расширять",
    meaning: "Полноценное приложение или полноценный сайт — на выбор.",
    subtitle:
      "Всё из BASE, расширенный конструктор и дополнительные расширения для выбранного канала: приложения или сайта.",
    idea: "Выберите один канал для расширенного конструирования. В варианте «Приложение» добавляйте страницы, расширения и сценарии приложения. В варианте «Сайт» создавайте полноценный сайт, сохраняя стандартное приложение BASE.",
    visual: "Система управления + расширенное приложение или сайт",
    benefits: [
      "Всё из BASE",
      "Полноценное приложение или полноценный сайт",
      "Расширенный конструктор выбранного канала",
      "Расширения и клиентские сценарии",
      "При выборе сайта стандартное приложение сохраняется",
    ],
    clientAccess: "Один расширенный канал на выбор: приложение или сайт. Стандартное приложение доступно в обоих вариантах.",
    ai: "AI-помощь для контента и клиентский AI-помощник по токенной модели",
    aiBalance: "$5 в месяц включено",
    storage: { internal: "3 ГБ", external: "3 ГБ" },
    modules: "Доступны",
    cta: "Выбрать PRO",
    micro: "Когда бизнес хочет дать клиенту собственный полноценный цифровой канал.",
  },
  {
    key: "proPlus",
    name: "PRO+",
    ladder: "Объединять",
    meaning: "Полноценное приложение + полноценный сайт. Одна система.",
    subtitle:
      "Расширенные приложение и сайт одновременно, с общими CRM, каталогом, клиентами, лояльностью и данными.",
    idea: "Сайт привлекает. Приложение удерживает. VCENTRE.ONE связывает всё вместе.",
    badge: "Оптимальный",
    visual: "Desktop + смартфон + сайт",
    benefits: [
      "Всё из PRO",
      "Полноценное приложение и сайт одновременно",
      "Расширенный конструктор обоих каналов",
      "Расширения и клиентские сценарии",
      "Единые CRM, каталог, клиенты и данные",
    ],
    clientAccess: "Приложение и сайт как единый клиентский контур.",
    ai: "AI-помощь для контента и клиентский AI-помощник по токенной модели",
    aiBalance: "$7 в месяц включено",
    storage: { internal: "4 ГБ", external: "4 ГБ" },
    modules: "Доступны",
    cta: "Выбрать PRO+",
    micro: "Когда нужен весь собственный digital-контур бизнеса.",
  },
  {
    key: "genius",
    name: "GENIUS",
    ladder: "Развивать с AI",
    meaning: "Всё из PRO+ + AI, который помогает управлять бизнесом.",
    subtitle: "Всё из PRO+ и внутренний AI-помощник для владельца и команды.",
    idea: "AI-помощь с контентом и клиентский помощник есть уже в BASE. GENIUS добавляет AI для анализа бизнеса, процессов и клиентской базы, управленческих рекомендаций и автоматизации.",
    badge: "Максимальный уровень",
    visual: "Вся экосистема + AI-помощник внутри платформы",
    benefits: [
      "Всё из PRO+",
      "AI-помощник для владельца и команды",
      "Анализ бизнеса, процессов и клиентской базы",
      "Поиск проблем, отклонений и точек внимания",
      "Управленческие рекомендации и AI-автоматизация",
    ],
    clientAccess: "Приложение, сайт и внутренний AI-слой для бизнеса.",
    ai: "Клиентский AI + внутренний AI-помощник для бизнеса и команды",
    aiBalance: "$10 в месяц включено",
    storage: { internal: "5 ГБ", external: "5 ГБ" },
    modules: "Доступны",
    cta: "Выбрать GENIUS",
    micro: "Когда AI должен помогать уже не только клиенту, но и вашему бизнесу.",
  },
];

export const GENIUS_AI_EXAMPLES = [
  "17 сделок стоят без движения больше трёх дней.",
  "У 8 сделок нет следующей задачи.",
  "Создать задачи ответственным?",
];

export const LADDER = [
  { name: "BASE", label: "Система + стандартное приложение" },
  { name: "PRO", label: "Расширенное приложение или сайт" },
  { name: "PRO+", label: "Расширенное приложение + сайт" },
  { name: "GENIUS", label: "+ AI для бизнеса" },
];

export const HOME_PLANS = PLANS.map(({ key, name, meaning }) => ({ key, name, meaning }));

type Cell = string;

export const COMPARE_GROUPS: { title: string; rows: { label: string; values: [Cell, Cell, Cell, Cell] }[] }[] = [
  {
    title: "Работа команды",
    rows: [
      { label: "Система управления", values: ["✓", "✓", "✓", "✓"] },
      { label: "Desktop + mobile для команды", values: ["✓", "✓", "✓", "✓"] },
      { label: "CRM и клиенты", values: ["✓", "✓", "✓", "✓"] },
      { label: "Онлайн + офлайн клиенты", values: ["✓", "✓", "✓", "✓"] },
      { label: "Заявки, сделки, задачи", values: ["✓", "✓", "✓", "✓"] },
      { label: "Сотрудники, роли и права", values: ["✓", "✓", "✓", "✓"] },
    ],
  },
  {
    title: "Стандартное приложение",
    rows: [
      { label: "Готовое клиентское приложение", values: ["✓", "✓", "✓", "✓"] },
      { label: "Главная системная страница", values: ["✓", "✓", "✓", "✓"] },
      { label: "Каталог", values: ["✓", "✓", "✓", "✓"] },
      { label: "Личный кабинет", values: ["✓", "✓", "✓", "✓"] },
      { label: "Лояльность", values: ["✓", "✓", "✓", "✓"] },
      { label: "Реферальная система", values: ["✓", "✓", "✓", "✓"] },
      { label: "Системные документы", values: ["✓", "✓", "✓", "✓"] },
      { label: "Брендирование и экран загрузки с логотипом", values: ["✓", "✓", "✓", "✓"] },
      { label: "Каталог без лимита по количеству позиций", values: ["✓", "✓", "✓", "✓"] },
    ],
  },
  {
    title: "Расширенный клиентский контур",
    rows: [
      { label: "Полноценный конструктор приложения", values: ["—", "На выбор", "✓", "✓"] },
      { label: "Полноценный сайт", values: ["—", "На выбор", "✓", "✓"] },
      { label: "Дополнительные клиентские расширения", values: ["—", "✓", "✓", "✓"] },
      { label: "Расширенные клиентские сценарии", values: ["—", "✓", "✓", "✓"] },
      { label: "Приложение + сайт одновременно в полном режиме", values: ["—", "—", "✓", "✓"] },
    ],
  },
  {
    title: "AI",
    rows: [
      { label: "AI-помощь при создании контента", values: ["✓", "✓", "✓", "✓"] },
      { label: "Клиентский AI-помощник", values: ["✓", "✓", "✓", "✓"] },
      { label: "AI для владельца и команды", values: ["—", "—", "—", "✓"] },
      { label: "AI-анализ бизнеса и процессов", values: ["—", "—", "—", "✓"] },
      { label: "Управленческие AI-рекомендации", values: ["—", "—", "—", "✓"] },
      { label: "AI-баланс / месяц", values: ["$1", "$5", "$7", "$10"] },
    ],
  },
  {
    title: "Ресурсы и расширение",
    rows: [
      { label: "Внутреннее хранилище", values: ["1 ГБ", "3 ГБ", "4 ГБ", "5 ГБ"] },
      { label: "Внешнее хранилище", values: ["2 ГБ", "3 ГБ", "4 ГБ", "5 ГБ"] },
    ],
  },
];

export const STORAGE_KINDS = [
  {
    title: "Внутренние данные",
    text: "CRM, клиенты, настройки, системные сущности, данные платформы, внутренние процессы и служебные данные.",
  },
  {
    title: "Внешние данные",
    text: "Файлы, изображения, документы, вложения, файлы чатов, медиа и другие загружаемые материалы.",
  },
];

export const AI_BALANCE = [
  { plan: "BASE", value: "$1" },
  { plan: "PRO", value: "$5" },
  { plan: "PRO+", value: "$7" },
  { plan: "GENIUS", value: "$10" },
];

export const PRO_CHANNEL_NOTE = "В PRO выбирается один полноценный канал: приложение или сайт. Если выбран сайт, стандартное приложение BASE сохраняется. PRO+ и GENIUS включают оба расширенных канала одновременно.";

export const DEMO = {
  title: "Начать можно с DEMO",
  text: "Получите готовое стандартное приложение, настройте бренд, страницы и каталог, попробуйте систему управления до оплаты. В DEMO приложение доступно для настройки и просмотра. Публикация для реальных клиентов открывается после подключения тарифа.",
};

export const AI_LEVELS = [
  { title: "AI-помощь для контента", text: "Во всех платных тарифах: тексты, карточки каталога, описания услуг и изображения." },
  { title: "Клиентский AI-помощник", text: "С BASE: помогает клиентам бизнеса внутри клиентского контура по токенной модели." },
  { title: "GENIUS AI", text: "Для владельца и команды: анализ бизнеса, CRM и процессов, поиск проблем, управленческие рекомендации и AI-автоматизация." },
];

export const EXTRA_WORK = [
  {
    num: "01",
    title: "Дизайн и оформление клиентского контура",
    text: "Индивидуальный дизайн страниц приложения или сайта и последующие изменения. Самостоятельное брендирование стандартного приложения доступно во всех тарифах; условия включённых работ указаны ниже.",
    tiers: [
      { period: "1–5 месяцев", lines: ["Первичная разработка — 5 000 ₽ / страница", "Изменения и редизайн — 2 500 ₽ / страница"] },
      { period: "6–11 месяцев", lines: ["Главная страница — бесплатно", "Остальные страницы — 3 000 ₽ / страница", "Изменения и редизайн — 1 500 ₽ / страница"] },
      { period: "12 месяцев и более", lines: ["Первичный дизайн приложения — включён", "Изменения и редизайн — 1 000 ₽ / страница"] },
    ],
  },
  {
    num: "02",
    title: "Настройка бизнес-процессов и сценариев",
    text: "Помогаем собрать реальные процессы бизнеса внутри VCENTRE.ONE.",
    tiers: [
      { period: "1–5 месяцев", lines: ["3 000 ₽ за каждый сценарий"] },
      { period: "6–11 месяцев", lines: ["1 сценарий включён", "Следующие — 1 500 ₽ за сценарий"] },
      { period: "12 месяцев и более", lines: ["10 сценариев включено", "Следующие — 1 000 ₽ за сценарий"] },
    ],
  },
  {
    num: "03",
    title: "Интеграции с внешними сервисами",
    text: "Стоимость работ рассчитывается по фактически затраченному времени.",
    tiers: [
      { period: "1–5 месяцев", lines: ["2 000 ₽ / час"] },
      { period: "6–11 месяцев", lines: ["1 800 ₽ / час"] },
      { period: "12 месяцев и более", lines: ["1 500 ₽ / час"] },
    ],
    note: "Стоимость конкретной интеграции определяется после оценки API, состава передаваемых данных и необходимой бизнес-логики.",
  },
];

export const YEAR_BENEFITS = [
  "Минимальная стоимость тарифа",
  "Первичный дизайн приложения включён",
  "10 бизнес-сценариев настройки включены",
  "Интеграционные работы — 1 500 ₽ / час",
  "Изменения дизайна — 1 000 ₽ / страница",
];

export const COST_PARTS = [
  { num: "01", title: "Тариф", text: "Уровень платформы и период использования." },
  { num: "02", title: "AI сверх включённого баланса", text: "Только если вы используете AI больше включённого объёма." },
  { num: "03", title: "Расширения", text: "Только если нужны — подключаются с PRO." },
  { num: "04", title: "Настройка, дизайн, интеграции", text: "Только если такие работы требуются по проекту." },
];

export const PICKER_STEPS: { question: string; answer: string; plan: PlanKey; result: string }[] = [
  {
    question: "Нужны система управления и готовое приложение?",
    answer: "CRM, команда и стандартное приложение с каталогом, кабинетом и лояльностью.",
    plan: "base",
    result: "BASE",
  },
  {
    question: "Нужно расширенное приложение или полноценный сайт?",
    answer: "Выберите один расширенный канал. Стандартное приложение остаётся в любом варианте.",
    plan: "pro",
    result: "PRO",
  },
  {
    question: "Нужны расширенное приложение и сайт одновременно?",
    answer: "Сайт привлекает, приложение удерживает, данные в одной системе.",
    plan: "proPlus",
    result: "PRO+",
  },
  {
    question: "Хотите AI-помощника для команды и владельца?",
    answer: "AI анализирует процессы и подсказывает следующие действия.",
    plan: "genius",
    result: "GENIUS",
  },
];

export const FAQ: { q: string; a: string }[] = [
  {
    q: "Есть ли приложение на BASE?",
    a: "Да. Готовое стандартное приложение включает главную, каталог, личный кабинет, лояльность, реферальную программу и системные документы. Его можно оформить в стиле своего бренда, наполнить и опубликовать для клиентов.",
  },
  {
    q: "Есть ли ограничения на количество товаров или услуг?",
    a: "Количество товаров, услуг и категорий каталога не ограничивается тарифом. Ресурсные ограничения связаны с объёмом хранилища и техническими возможностями платформы.",
  },
  {
    q: "Есть ли ограничения на количество воронок?",
    a: "Нет. Тарифная модель не строится вокруг искусственного ограничения числа воронок, товаров, клиентов или сотрудников. Основное ресурсное ограничение — объём хранилища.",
  },
  {
    q: "Чем BASE отличается от PRO?",
    a: "BASE включает систему управления и готовое стандартное приложение. PRO добавляет расширенный конструктор, дополнительные расширения и выбор одного полноценного канала: приложения или сайта.",
  },
  {
    q: "Что значит «приложение или сайт» в PRO?",
    a: "Это две конфигурации одного тарифа с одинаковой ценой. Вы выбираете расширенное приложение или полноценный сайт. При выборе сайта стандартное приложение BASE остаётся доступным и работает.",
  },
  {
    q: "Что входит в PRO+?",
    a: "Полноценное приложение и полноценный сайт одновременно: расширенный конструктор обоих каналов, общие CRM, каталог, клиентская база, лояльность и данные. Индивидуальные работы и дополнительные интеграции оцениваются отдельно.",
  },
  {
    q: "Чем GENIUS отличается от PRO+?",
    a: "GENIUS включает всё из PRO+ и добавляет AI для владельца и команды: анализ бизнеса, данных и процессов, поиск проблем, управленческие рекомендации и AI-автоматизацию. AI-помощь с контентом и клиентский помощник доступны уже в BASE.",
  },
  {
    q: "Есть ли AI в BASE?",
    a: "Да. AI помогает создавать тексты, карточки каталога и изображения; клиентский AI-помощник работает по токенной модели. В BASE включён AI-баланс $1 в месяц. AI для анализа и управления бизнесом добавляется в GENIUS.",
  },
  {
    q: "Как оплачивается AI?",
    a: "Каждый месяц включён AI-баланс: BASE — $1, PRO — $5, PRO+ — $7, GENIUS — $10. Дополнительное потребление оплачивается по токенной модели: баланс можно пополнить без смены тарифа.",
  },
  {
    q: "Можно ли подключить дополнительные расширения к BASE?",
    a: "Стандартные страницы приложения, включая каталог, кабинет, лояльность, рефералы и документы, уже входят в BASE. Дополнительные клиентские расширения доступны с PRO для выбранного расширенного канала и оплачиваются отдельно.",
  },
  {
    q: "Есть ли ограничения по хранилищу?",
    a: "Да. У каждого тарифа свой объём внутренних и внешних данных: BASE 1 + 2 ГБ, PRO 3 + 3 ГБ, PRO+ 4 + 4 ГБ, GENIUS 5 + 5 ГБ.",
  },
  {
    q: "Можно ли сначала всё настроить, а потом оплатить?",
    a: "Да. В DEMO уже есть стандартное приложение: можно настроить бренд, страницы и каталог, попробовать систему управления и посмотреть результат. Публикация приложения для реальных клиентов становится доступна после подключения тарифа.",
  },
  {
    q: "Входит ли дизайн приложения?",
    a: "Самостоятельное брендирование стандартных страниц доступно во всех тарифах через глобальную дизайн-систему. Индивидуальные работы команды зависят от срока договора: от 12 месяцев первичный дизайн приложения включён, изменения — 1 000 ₽ за страницу.",
  },
  {
    q: "Сколько стоят интеграции?",
    a: "По часовой ставке, которая зависит от срока договора: 2 000 / 1 800 / 1 500 ₽ в час. Итоговая стоимость определяется после технической оценки.",
  },
  {
    q: "Можно ли вести клиента, если у него нет приложения?",
    a: "Да. VCENTRE.ONE полноценно работает с онлайн- и офлайн-клиентами: менеджер создаёт клиента вручную, история и коммуникации остаются в системе.",
  },
];
